import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesApi } from '@/lib/api';
import type { CustomRoleInfo, PermissionInfo } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Plus, Trash2, Save, X, Shield, Check, Pencil, ChevronDown, ChevronUp
} from 'lucide-react';

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6',
  '#d946ef', '#ec4899', '#6b7280', '#1e293b',
];

interface CustomRoleManagerProps {
  allPermissions: PermissionInfo[];
  customRoles: CustomRoleInfo[];
  isOwner: boolean;
}

export function CustomRoleManager({ allPermissions, customRoles, isOwner }: CustomRoleManagerProps) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [newRole, setNewRole] = useState({
    name: '',
    display_name: '',
    description: '',
    color: '#3b82f6',
    permissions: [] as string[],
  });
  const [editData, setEditData] = useState<{
    display_name: string;
    description: string;
    color: string;
    permissions: string[];
  }>({
    display_name: '',
    description: '',
    color: '#6b7280',
    permissions: [],
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof newRole) => rolesApi.createCustomRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsCreating(false);
      setNewRole({ name: '', display_name: '', description: '', color: '#3b82f6', permissions: [] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof editData }) =>
      rolesApi.updateCustomRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rolesApi.deleteCustomRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const groupedPermissions = allPermissions.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {} as Record<string, PermissionInfo[]>);

  const togglePermission = (
    permissions: string[],
    code: string,
    setter: (perms: string[]) => void
  ) => {
    if (permissions.includes(code)) {
      setter(permissions.filter((p) => p !== code));
    } else {
      setter([...permissions, code]);
    }
  };

  const startEdit = (role: CustomRoleInfo) => {
    setEditingId(role.id);
    setEditData({
      display_name: role.display_name,
      description: role.description,
      color: role.color,
      permissions: [...role.permissions],
    });
  };

  if (!isOwner) return null;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-violet-400" />
          <h3 className="text-lg font-semibold text-white">Custom Roles</h3>
          <span className="text-xs text-zinc-500 ml-2">
            {customRoles.length} custom role{customRoles.length !== 1 ? 's' : ''}
          </span>
        </div>
        {!isCreating && (
          <Button
            size="sm"
            onClick={() => setIsCreating(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white gap-1"
          >
            <Plus className="w-4 h-4" />
            New Role
          </Button>
        )}
      </div>

      {/* Create form */}
      {isCreating && (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-zinc-300 mb-3">Create New Custom Role</h4>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Name (slug)</label>
              <input
                type="text"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_') })}
                placeholder="e.g. lead_developer"
                className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-600 rounded text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Display Name</label>
              <input
                type="text"
                value={newRole.display_name}
                onChange={(e) => setNewRole({ ...newRole, display_name: e.target.value })}
                placeholder="e.g. Lead Developer"
                className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-600 rounded text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs text-zinc-400 block mb-1">Description</label>
            <input
              type="text"
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              placeholder="What this role is for..."
              className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-600 rounded text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          </div>
          <div className="mb-3">
            <label className="text-xs text-zinc-400 block mb-1">Color</label>
            <div className="flex gap-1.5 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setNewRole({ ...newRole, color: c })}
                  className="w-6 h-6 rounded-full border-2 transition-all"
                  style={{
                    backgroundColor: c,
                    borderColor: newRole.color === c ? 'white' : 'transparent',
                    transform: newRole.color === c ? 'scale(1.2)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs text-zinc-400 block mb-2">Permissions</label>
            {Object.entries(groupedPermissions).map(([category, perms]) => (
              <div key={category} className="mb-2">
                <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{category}</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {perms.map((p) => {
                    const active = newRole.permissions.includes(p.code);
                    return (
                      <button
                        key={p.code}
                        onClick={() => togglePermission(newRole.permissions, p.code, (ps) => setNewRole({ ...newRole, permissions: ps }))}
                        className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
                          active
                            ? 'bg-violet-600/30 border-violet-500 text-violet-300'
                            : 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500'
                        }`}
                        title={p.description}
                      >
                        {p.code.split('.').slice(1).join('.')}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setIsCreating(false)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => createMutation.mutate(newRole)}
              disabled={!newRole.name || !newRole.display_name || createMutation.isPending}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Save className="w-4 h-4 mr-1" /> Create
            </Button>
          </div>
        </div>
      )}

      {/* Roles list */}
      {customRoles.length === 0 && !isCreating && (
        <div className="text-center py-8 text-zinc-500 text-sm border border-dashed border-zinc-700 rounded-lg">
          No custom roles yet. Click "New Role" to create one.
        </div>
      )}

      <div className="space-y-2">
        {customRoles.map((role) => {
          const isEditing = editingId === role.id;
          const isExpanded = expandedId === role.id;

          return (
            <div
              key={role.id}
              className="bg-zinc-800/40 border border-zinc-700/60 rounded-lg overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center gap-3 px-4 py-3">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: role.color }}
                />
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.display_name}
                      onChange={(e) => setEditData({ ...editData, display_name: e.target.value })}
                      className="bg-zinc-900 border border-zinc-600 rounded px-2 py-0.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                    />
                  ) : (
                    <span className="text-sm font-medium text-white">{role.display_name}</span>
                  )}
                  <span className="text-xs text-zinc-500 ml-2">({role.name})</span>
                </div>
                <span className="text-xs text-zinc-500">
                  {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-1">
                  {isEditing ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingId(null)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateMutation.mutate({ id: role.id, data: editData })}
                        disabled={updateMutation.isPending}
                        className="h-7 px-2 bg-violet-600 hover:bg-violet-700 text-xs"
                      >
                        <Save className="w-3.5 h-3.5 mr-1" /> Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedId(isExpanded ? null : role.id)}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                      >
                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { startEdit(role); setExpandedId(role.id); }}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-blue-400"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (confirm(`Delete role "${role.display_name}"? Users with this role will be reset to Member.`)) {
                            deleteMutation.mutate(role.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded content */}
              {(isExpanded || isEditing) && (
                <div className="px-4 pb-4 border-t border-zinc-700/40">
                  {isEditing && (
                    <div className="mt-3 mb-3">
                      <label className="text-xs text-zinc-400 block mb-1">Description</label>
                      <input
                        type="text"
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-600 rounded text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                      <label className="text-xs text-zinc-400 block mb-1 mt-2">Color</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {PRESET_COLORS.map((c) => (
                          <button
                            key={c}
                            onClick={() => setEditData({ ...editData, color: c })}
                            className="w-5 h-5 rounded-full border-2 transition-all"
                            style={{
                              backgroundColor: c,
                              borderColor: editData.color === c ? 'white' : 'transparent',
                              transform: editData.color === c ? 'scale(1.2)' : 'scale(1)',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-3">
                    <label className="text-xs text-zinc-400 block mb-2">Permissions</label>
                    {Object.entries(groupedPermissions).map(([category, perms]) => (
                      <div key={category} className="mb-2">
                        <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{category}</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {perms.map((p) => {
                            const activePerms = isEditing ? editData.permissions : role.permissions;
                            const active = activePerms.includes(p.code);
                            return (
                              <button
                                key={p.code}
                                onClick={() => {
                                  if (isEditing) {
                                    togglePermission(editData.permissions, p.code, (ps) =>
                                      setEditData({ ...editData, permissions: ps })
                                    );
                                  }
                                }}
                                disabled={!isEditing}
                                className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
                                  active
                                    ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-400'
                                    : isEditing
                                      ? 'bg-zinc-800 border-zinc-600 text-zinc-400 hover:border-zinc-500 cursor-pointer'
                                      : 'bg-zinc-800/50 border-zinc-700 text-zinc-500'
                                } ${!isEditing ? 'cursor-default' : ''}`}
                                title={p.description}
                              >
                                {active && <Check className="w-3 h-3 inline mr-0.5" />}
                                {p.code.split('.').slice(1).join('.')}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
